import React from "react";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from "recharts";

import { FIXED_TOKENS } from "../../../whiteLabel/whiteLabelTheme";

import classes from "./MacroTriangle.module.css";

/** Display-ready vertex: the caller resolves names, units and formatting. */
export type MacroNode = {
  key: string;
  /** Localized macronutrient name shown inside the circle. */
  name: string;
  /** Share of total intake, drives the polygon. */
  value: number;
  percent: string;
  grams: string;
  kcal: string;
};

interface Props {
  caption: string;
  nodes: MacroNode[];
  width?: number;
  height?: number;
}

/** Kept in sync with the outerRadius passed to RadarChart below. */
const OUTER_RADIUS_RATIO = 0.62;

/**
 * Protein / Carbs / Fat proportion triangle.
 *
 * Uses Recharts directly rather than the shared CustomRadarChart because that
 * component (a) hardcodes `<radialGradient id="color">` and `fill="url(#color)"`,
 * so two instances on one page collide on the duplicate DOM id, (b) declares its
 * gradient stops as `var(--secondary19)` — CSS variables are not substituted in
 * SVG presentation attributes — and (c) positions its labels absolutely for a
 * 440px-wide chart. It is also shared with four other pages, so widening it here
 * would carry avoidable risk.
 *
 * With startAngle 90 / endAngle -270 and three points, the vertices land at top,
 * bottom-right and bottom-left in data order.
 */
const MacroTriangle = ({
  caption,
  nodes,
  width = 300,
  height = 236,
}: Props): JSX.Element => {
  const chartData = nodes.map((node) => ({
    NAME: node.name,
    VALUE: node.value,
  }));

  // Anchor each label on its actual grid vertex instead of guessing percentages.
  // Recharts centres the polar chart in the box and derives the radius from the
  // shorter side, so the three polygon corners are exactly:
  //   top          (cx,               cy - r)
  //   bottom-right (cx + r*cos30,     cy + r/2)
  //   bottom-left  (cx - r*cos30,     cy + r/2)
  const cx = width / 2;
  const cy = height / 2;
  const r = OUTER_RADIUS_RATIO * (Math.min(width, height) / 2);
  const dx = r * Math.cos(Math.PI / 6);
  const dy = r / 2;

  // Nudge the lower labels outboard so they clear the triangle's own edges.
  const SPREAD = 22;
  const NODE_H = 30;

  const positions: React.CSSProperties[] = [
    { left: cx, top: cy - r - NODE_H - 2, transform: "translate(-50%, 0)" },
    { left: cx + dx + SPREAD, top: cy + dy + 8, transform: "translate(-50%, 0)" },
    { left: cx - dx - SPREAD, top: cy + dy + 8, transform: "translate(-50%, 0)" },
  ];

  return (
    <div className={classes.wrap}>
      <div className={classes.caption}>{caption}</div>

      <div className={classes.chartBox} style={{ width, height }}>
        <RadarChart
          width={width}
          height={height}
          data={chartData}
          outerRadius={`${OUTER_RADIUS_RATIO * 100}%`}
          startAngle={90}
          endAngle={-270}
        >
          <PolarGrid stroke={FIXED_TOKENS.ruleStrong} gridType="polygon" />
          <PolarAngleAxis dataKey="NAME" tick={false} />
          <PolarRadiusAxis domain={[0, 65]} opacity={0} tickCount={0} />
          <Radar
            isAnimationActive={false}
            dataKey="VALUE"
            stroke="transparent"
            /* Literal hex: a var() here would not resolve in the SVG attribute. */
            fill={FIXED_TOKENS.triangleFill}
            fillOpacity={0.85}
            dot={{
              stroke: FIXED_TOKENS.ruleStrong,
              strokeWidth: 1,
              r: 3,
              fill: "#ffffff",
            }}
          />
        </RadarChart>

        {nodes.map((node, index) => (
          <div className={classes.node} key={node.key} style={positions[index]}>
            <span className={classes.kcal}>{node.kcal}</span>
            <span className={classes.pill}>
              <span className={classes.side}>{node.percent}</span>
              <span className={classes.circle}>{node.name}</span>
              <span className={classes.side}>{node.grams}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MacroTriangle;
